import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { listDailyWorkForVendor } from "./daily-work.api";
import { DailyWorkJoin } from "@/repositories/daily-work.repository";
import { ReadAllResponse } from "@/interfaces";
import { daily_work_status } from "@/models/daily-work.model";

interface FilterState {
  status: string;
  search: string;
  sortKey: string;
  sortOrder: "asc" | "desc";
}

interface PaginationState {
  page: number;
  pageSize: number;
}

interface DailyWorkState {
  list: DailyWorkJoin[];
  loading: boolean;
  error: string | null;
  total: number;
  filters: FilterState;
  pagination: PaginationState;
}

const initialState: DailyWorkState = {
  list: [],
  loading: false,
  error: null,
  total: 0,
  filters: {
    status: "",
    search: "",
    sortKey: "created_at",
    sortOrder: "desc",
  },
  pagination: { page: 1, pageSize: 10 },
};

export const fetchDailyWorkForVendor = createAsyncThunk("dailyWork/fetchForVendor", async (_, { getState }) => {
  const s = (getState() as { dailyWork: DailyWorkState }).dailyWork;
  const { page, pageSize } = s.pagination;
  const { status, search, sortKey, sortOrder } = s.filters;

  return await listDailyWorkForVendor(
    page,
    pageSize,
    {
      status: status as daily_work_status | "",
      search,
    },
    sortKey,
    sortOrder
  );
});

const slice = createSlice({
  name: "dailyWork",
  initialState,
  reducers: {
    setStatus(s, a: PayloadAction<string>) {
      s.filters.status = a.payload;
      s.pagination.page = 1;
    },
    setSearch(s, a: PayloadAction<string>) {
      s.filters.search = a.payload;
      s.pagination.page = 1;
    },

    setPage(s, a: PayloadAction<number>) {
      s.pagination.page = a.payload;
    },
    setPageSize(s, a: PayloadAction<number>) {
      s.pagination.pageSize = a.payload;
    },

    setSortKey(s, a: PayloadAction<string>) {
      if (s.filters.sortKey === a.payload) {
        s.filters.sortOrder = s.filters.sortOrder === "asc" ? "desc" : "asc";
      } else {
        s.filters.sortKey = a.payload;
        s.filters.sortOrder = "asc";
      }
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchDailyWorkForVendor.pending, (s) => {
      s.loading = true;
    });
    b.addCase(fetchDailyWorkForVendor.fulfilled, (s, a: PayloadAction<ReadAllResponse<DailyWorkJoin>>) => {
      s.loading = false;
      s.list = a.payload.data;
      s.total = a.payload.count ?? 0;
    });
    b.addCase(fetchDailyWorkForVendor.rejected, (s, a) => {
      if (a.meta.aborted) return;
      s.loading = false;
      s.error = a.error.message || "Failed to fetch daily work.";
    });
  },
});

export const { setStatus, setSearch, setPage, setPageSize, setSortKey } = slice.actions;

export default slice.reducer;
